import { useMemo, useState } from 'react';
import './FaqPage.css';

const CATEGORIES = [
  {
    id: 'customers',
    label: 'For Customers',
    tone: 'green',
    questions: [
      {
        q: 'How does SnapScrap work?',
        a: 'Simply post your unwanted scrap metal, upload a few photos and provide your collection details. Your job is then made available to licensed local scrap collectors. Once a collector accepts it, you’ll receive updates about your collection.',
      },
      {
        q: 'Is it free to post my scrap?',
        a: 'Yes. Posting your unwanted scrap metal on SnapScrap is free.',
      },
      {
        q: 'How quickly can my scrap be collected?',
        a: 'Collection times depend on collector availability and your location. Once your job is accepted, you’ll receive updates so you know what’s happening.',
      },
      {
        q: 'What types of scrap do you collect?',
        a: 'SnapScrap is designed for unwanted metal and scrap items such as washing machines, cookers, dishwashers, radiators, boilers, bikes, metal gates, metal doors, car batteries and other suitable metal items.',
      },
      {
        q: 'Do you collect fridges and freezers?',
        a: 'No. Fridges and freezers are not currently accepted through SnapScrap.',
      },
      {
        q: 'Do I need to move the scrap outside?',
        a: 'Not necessarily. When posting your scrap, tell us where the items are located and provide any important access information so the collector knows what to expect.',
      },
      {
        q: 'Do I need to be at home for collection?',
        a: 'Not always. If the scrap can be safely accessed without you being there, you can provide collection and access instructions when posting the job.',
      },
      {
        q: 'Can I leave the scrap outside for collection?',
        a: 'Yes, provided the items are on your property and the collector can access them safely. Tell the collector exactly where the items have been left.',
      },
      {
        q: 'Can I upload photos of my scrap?',
        a: 'Yes. Photos are an important part of your SnapScrap post and help collectors understand what needs collecting before accepting the job.',
      },
      {
        q: 'Can I track my collection?',
        a: 'Once a collector has accepted your job, SnapScrap keeps you updated on the progress of your collection.',
      },
      {
        q: 'Who will collect my scrap?',
        a: 'Jobs are made available to registered scrap collectors using the SnapScrap platform. Collector details are provided once your collection has been accepted.',
      },
      {
        q: 'What happens after a collector accepts my job?',
        a: 'You’ll receive confirmation that your job has been accepted. The collector can then travel to your location, complete the collection and update the job through SnapScrap.',
      },
      {
        q: 'What if nobody accepts my collection?',
        a: 'Acceptance depends on factors such as your location, the items posted and collector availability. If your job has not yet been accepted, it can remain available for suitable collectors to see.',
      },
      {
        q: 'Can businesses use SnapScrap?',
        a: 'Yes. SnapScrap can also be used by businesses that regularly generate unwanted metal and scrap.',
      },
      {
        q: 'What happens to my scrap after it is collected?',
        a: 'The collector takes responsibility for the scrap they collect and handles it through the appropriate recycling or disposal routes.',
      },
    ],
  },
  {
    id: 'collectors',
    label: 'For Scrap Collectors',
    tone: 'slate',
    questions: [
      {
        q: 'How do I become a SnapScrap collector?',
        a: 'Register as a collector and provide the required business and licence information for verification.',
      },
      {
        q: 'Do I need a waste carrier licence?',
        a: 'Collectors must meet SnapScrap’s eligibility and verification requirements before collecting jobs through the platform.',
      },
      {
        q: 'How do I receive new jobs?',
        a: 'Available scrap jobs in your operating area can be shown through the collector platform, allowing you to review the job information before deciding whether to accept.',
      },
      {
        q: 'Can I see the scrap before accepting a job?',
        a: 'Yes. Where provided by the customer, you can review photos and relevant job information before accepting.',
      },
      {
        q: 'What happens when I accept a job?',
        a: 'Once the job is successfully assigned to you, you receive the collection details needed to complete it.',
      },
      {
        q: 'Can multiple collectors accept the same job?',
        a: 'No. Once a job has been successfully assigned, it is no longer available for another collector to take.',
      },
      {
        q: 'Do collectors keep the scrap they collect?',
        a: 'Yes. Subject to applicable laws and the terms of the collection, the collector takes the scrap collected and can process or sell it through their normal authorised channels.',
      },
      {
        q: 'Can I choose which jobs I accept?',
        a: 'Yes. You can review available jobs and decide which collections are suitable for your business.',
      },
      {
        q: 'Can I choose the areas I work in?',
        a: 'Collectors can operate within the areas supported by their SnapScrap account and service settings.',
      },
      {
        q: 'Why should I join SnapScrap?',
        a: 'SnapScrap is designed to connect licensed collectors with people and businesses that already have unwanted scrap ready for collection — helping you spend less time searching for work and more time collecting.',
      },
    ],
  },
];

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.7" />
      <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function ChevronIcon({ open }) {
  return (
    <svg
      className={open ? 'is-open' : undefined}
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2.5 4.5L6 8l3.5-3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function QuestionMarkIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M9.8 9.5a2.2 2.2 0 1 1 3.5 1.7c-.7.6-1.3 1.1-1.3 2.1"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <circle cx="12" cy="16.2" r="0.9" fill="currentColor" />
    </svg>
  );
}

function CustomersCatIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M5.5 19.2c.7-3.2 3.3-5 6.5-5s5.8 1.8 6.5 5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CollectorsCatIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 16V8.5A1.5 1.5 0 0 1 4.5 7H14v9H3z"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path d="M14 10h4.2L21 13.2V16h-7v-6z" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="7" cy="16.5" r="1.7" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="17" cy="16.5" r="1.7" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

const CAT_ICONS = {
  customers: CustomersCatIcon,
  collectors: CollectorsCatIcon,
};

export default function FaqPage() {
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [openKey, setOpenKey] = useState(null);

  const totalCount = CATEGORIES.reduce((sum, cat) => sum + cat.questions.length, 0);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return CATEGORIES.map((cat) => {
      const questions = cat.questions.filter((item) => {
        if (activeFilter !== 'all' && activeFilter !== cat.id) return false;
        if (!q) return true;
        return (
          item.q.toLowerCase().includes(q) ||
          item.a.toLowerCase().includes(q) ||
          cat.label.toLowerCase().includes(q)
        );
      });
      return { ...cat, questions };
    }).filter((cat) => cat.questions.length > 0);
  }, [query, activeFilter]);

  const filters = [
    { id: 'all', label: 'All FAQs', count: totalCount },
    ...CATEGORIES.map((cat) => ({
      id: cat.id,
      label: cat.label,
      count: cat.questions.length,
    })),
  ];

  return (
    <section className="faqs-page" aria-labelledby="faqs-heading">
      <div className="faqs-inner">
        <header className="faqs-header">
          <h1 id="faqs-heading">Frequently Asked Questions</h1>
          <p>Answers for customers and scrap collectors</p>
        </header>

        <label className="faqs-search">
          <span className="visually-hidden">Search for a question</span>
          <input
            type="search"
            placeholder="Search for a question..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <SearchIcon />
        </label>

        <div className="faqs-filters" role="tablist" aria-label="FAQ categories">
          {filters.map((filter) => (
            <button
              key={filter.id}
              type="button"
              role="tab"
              aria-selected={activeFilter === filter.id}
              className={`faqs-filter ${activeFilter === filter.id ? 'is-active' : ''}`}
              onClick={() => setActiveFilter(filter.id)}
            >
              {filter.label}
              <span>{filter.count}</span>
            </button>
          ))}
        </div>

        <div className="faqs-groups">
          {filtered.length === 0 ? (
            <p className="faqs-empty">No questions match your search.</p>
          ) : (
            filtered.map((cat) => {
              const Icon = CAT_ICONS[cat.id];
              const countLabel = `${cat.questions.length} question${cat.questions.length === 1 ? '' : 's'}`;

              return (
                <section key={cat.id} className={`faqs-group tone-${cat.tone}`} aria-labelledby={`faq-cat-${cat.id}`}>
                  <div className="faqs-group-head">
                    <span className="faqs-group-icon">
                      <Icon />
                    </span>
                    <div>
                      <h2 id={`faq-cat-${cat.id}`}>{cat.label}</h2>
                      <p>{countLabel}</p>
                    </div>
                  </div>

                  <div className="faqs-items">
                    {cat.questions.map((item, index) => {
                      const key = `${cat.id}-${index}`;
                      const open = openKey === key;

                      return (
                        <div key={key} className={`faqs-item ${open ? 'is-open' : ''}`}>
                          <button
                            type="button"
                            className="faqs-item-trigger"
                            aria-expanded={open}
                            onClick={() => setOpenKey(open ? null : key)}
                          >
                            <span className="faqs-q-icon">
                              <QuestionMarkIcon />
                            </span>
                            <span className="faqs-q-text">{item.q}</span>
                            <span className="faqs-chevron">
                              <ChevronIcon open={open} />
                            </span>
                          </button>
                          {open && (
                            <div className="faqs-item-panel">
                              <p>{item.a}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
}
